import { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Plus, Edit3, Trash2, X, Loader2, RefreshCw, ExternalLink, FolderOpen } from 'lucide-react';
import { academyApi, PLAN_LABEL } from '@/services/academyApi';
import { useDashboard } from '@/context/DashboardContext';
import { useConfirm } from '@/components/dashboard/admin/ConfirmDialog';

/*
  ResourcesManager — material de la sección Marketing del panel de miembro
  (colección marketing_resources). Recursos con plan: los miembros sin ese plan ven el
  candado y el servidor no les envía la URL. Borrar: solo administradores.
*/

const inputCls = 'w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#c9a84c]/40';
const labelCls = 'text-[11px] uppercase tracking-wider text-gray-400 mb-1 block font-semibold';
const TYPES = ['PDF', 'Imagen', 'Vídeo', 'Plantilla', 'Enlace'];
// Tipos de los recursos antiguos → tipo actual
const LEGACY_TYPE = { MP4: 'Vídeo', PNG: 'Imagen', JPG: 'Imagen', ZIP: 'Enlace', HTML: 'Plantilla', DOCX: 'Plantilla' };
const PLAN_OPTIONS = [
  ['', 'Todos los registrados'],
  ['starter', 'Socios (Access o superior)'],
  ['basica', 'PRO o superior'],
  ['premium', 'Solo Premium'],
];
const isHttps = (u) => { try { return new URL(u).protocol === 'https:'; } catch { return false; } };

function ResourceModal({ resource, categories, onClose, onSaved }) {
  const isEditing = !!resource?.id;
  const legacyType = resource?.type && !TYPES.includes(resource.type) ? resource.type : null;
  const [form, setForm] = useState({
    title: resource?.title || '', category: resource?.category || '',
    type: legacyType ? (LEGACY_TYPE[legacyType] || 'Enlace') : (resource?.type || 'PDF'),
    url: resource?.url || resource?.file_url || '', size: resource?.size || '', description: resource?.description || '',
    min_plan: resource?.min_plan || '', published: resource ? resource.published !== false : true,
  });
  const [saving, setSaving] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.category.trim()) return toast.error('Título y categoría son obligatorios');
    if (form.url.trim() && !isHttps(form.url.trim())) return toast.error('La URL debe empezar por https://');
    const payload = {
      title: form.title.trim(), category: form.category.trim(), type: form.type, url: form.url.trim() || null,
      size: form.size.trim() || null, description: form.description.trim() || null, min_plan: form.min_plan || null, published: form.published,
    };
    setSaving(true);
    try {
      if (isEditing) await academyApi.adminUpdateResource(resource.id, payload);
      else await academyApi.adminCreateResource(payload);
      toast.success(isEditing ? 'Recurso guardado' : 'Recurso creado');
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="resource-modal-title">
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.15 }}
        className="bg-[#0d1829] border border-white/10 rounded-2xl w-full max-w-xl shadow-2xl my-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 id="resource-modal-title" className="text-white font-semibold">{isEditing ? 'Editar recurso' : 'Nuevo recurso de marketing'}</h3>
          <button type="button" onClick={onClose} aria-label="Cerrar"><X size={18} className="text-gray-400 hover:text-white" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          <div><label className={labelCls}>Título *</label><input value={form.title} onChange={set('title')} className={inputCls} maxLength={200} /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Categoría *</label>
              <input value={form.category} onChange={set('category')} className={inputCls} maxLength={80} list="resource-categories" placeholder="Presentaciones, Redes sociales…" />
              <datalist id="resource-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <div>
              <label className={labelCls}>Tipo</label>
              <select value={form.type} onChange={set('type')} className={inputCls}>
                {TYPES.map((t) => <option key={t} value={t} className="bg-[#0d1829]">{t}</option>)}
              </select>
              {legacyType && <p className="mt-1 text-[11px] text-amber-200">Tipo anterior: {legacyType}. Revisa el nuevo tipo antes de guardar.</p>}
            </div>
          </div>
          <div><label className={labelCls}>URL (https)</label><input value={form.url} onChange={set('url')} className={inputCls} placeholder="https://…" maxLength={1000} /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className={labelCls}>Tamaño</label><input value={form.size} onChange={set('size')} className={inputCls} maxLength={40} placeholder="4,2 MB" /></div>
            <div><label className={labelCls}>Acceso</label>
              <select value={form.min_plan} onChange={set('min_plan')} className={inputCls}>
                {PLAN_OPTIONS.map(([v, l]) => <option key={v} value={v} className="bg-[#0d1829]">{l}</option>)}
              </select>
            </div>
          </div>
          <div><label className={labelCls}>Descripción</label><textarea rows={3} value={form.description} onChange={set('description')} className={`${inputCls} resize-y`} maxLength={1000} /></div>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={form.published} onChange={set('published')} className="accent-[#c9a84c]" />
            Publicado (visible en Marketing)
          </label>
          {!form.url.trim() && <p className="text-xs text-gray-400">Sin URL, los miembros verán el recurso como «Próximamente».</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-white/10 text-sm text-gray-300 hover:text-white">Cancelar</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c9a84c] text-[#0a1628] text-sm font-semibold disabled:opacity-50" data-testid="resource-save">
              {saving && <Loader2 size={14} className="animate-spin" />} {isEditing ? 'Guardar cambios' : 'Crear recurso'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default function ResourcesManager() {
  const [dialog, ask] = useConfirm();
  const { user } = useDashboard();
  const canDelete = user?.role === 'admin';
  const [items, setItems] = useState(null);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    academyApi.adminResources().then(setItems).catch((e) => { toast.error(e.message); setItems([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => [...new Set((items || []).map((r) => r.category).filter(Boolean))].sort(), [items]);

  const remove = async (r) => {
    const ok = await ask({
      title: `Eliminar «${r.title}»`,
      description: 'Deja de verse en la sección Marketing de los miembros. No se puede deshacer.',
      confirmLabel: 'Eliminar recurso',
    });
    if (!ok) return;
    try {
      await academyApi.adminDeleteResource(r.id);
      toast.success('Recurso eliminado');
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (!items) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#c9a84c]" size={24} /></div>;

  return (
    <div className="space-y-4" data-testid="resources-manager">
      {dialog}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-400">{items.length} recursos en la sección Marketing</p>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 rounded-lg border border-white/10 text-gray-400 hover:text-white" aria-label="Recargar"><RefreshCw size={15} /></button>
          <button onClick={() => setModal({})} className="inline-flex items-center gap-2 rounded-lg bg-[#c9a84c] px-3 py-2 text-sm font-semibold text-[#0a1628]" data-testid="resource-new">
            <Plus size={15} /> Nuevo recurso
          </button>
        </div>
      </div>

      {!items.length ? (
        <div className="rounded-2xl border border-dashed border-white/[0.08] py-10 text-center text-sm text-gray-400">
          <FolderOpen size={22} className="mx-auto mb-2 text-[#c9a84c]" />
          Todavía no hay recursos. Añade presentaciones, imágenes o plantillas para los miembros.
        </div>
      ) : (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-white/[0.06] text-left text-[11px] uppercase tracking-wider text-gray-400">
                  {['Recurso', 'Categoría', 'Tipo', 'Acceso', 'Estado', ''].map((h) => <th key={h} scope="col" className="py-3 px-4 font-semibold">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-b border-white/[0.03] align-top">
                    <td className="py-3 px-4">
                      <p className="text-white">{r.title}</p>
                      <p className="text-xs text-gray-500">{[r.size, r.url || r.file_url ? null : 'Sin URL'].filter(Boolean).join(' · ')}</p>
                    </td>
                    <td className="py-3 px-4 text-gray-300">{r.category || '—'}</td>
                    <td className="py-3 px-4 text-gray-300">{r.type || '—'}</td>
                    <td className="py-3 px-4 text-gray-300">{r.min_plan ? PLAN_LABEL[r.min_plan] : 'Todos'}</td>
                    <td className="py-3 px-4">
                      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${r.published ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300' : 'border-amber-400/20 bg-amber-400/10 text-amber-200'}`}>
                        {r.published ? 'Publicado' : 'Borrador'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end gap-2">
                        {(r.url || r.file_url) && (
                          <a href={r.url || r.file_url} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg border border-white/10 text-gray-400 hover:text-white" aria-label={`Abrir ${r.title}`}><ExternalLink size={14} /></a>
                        )}
                        <button onClick={() => setModal(r)} className="p-2 rounded-lg border border-white/10 text-gray-400 hover:text-white" aria-label={`Editar ${r.title}`}><Edit3 size={14} /></button>
                        {canDelete && <button onClick={() => remove(r)} className="p-2 rounded-lg border border-white/10 text-gray-400 hover:text-red-300" aria-label={`Eliminar ${r.title}`}><Trash2 size={14} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && <ResourceModal resource={modal.id ? modal : null} categories={categories} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}
